import React, {useState, useEffect, Fragment} from 'react';
import {Tag, Button, MenuItem, Icon} from '@blueprintjs/core';
import {Suggest, Select} from '@blueprintjs/select';

import {Row, Cell, Table} from '/src/components/tables/table'
import {errorToast} from '/src/utils/toaster';
const IN_ACTION = [{direction: 'in', crossed: true}, {direction: 'out', crossed: false}];
const OUT_ACTION = [{direction: 'out', crossed: true}, {direction: 'in', crossed: false}];
const IN_AND_OUT_ACTION = [{direction: 'out', crossed: true}, {direction: 'in', crossed: true}];

const requiresMeta = (type) => type === 'within-location-entry' || type === 'within-location-pass-by';

const EditingItemForm = (
    {
        item: initialItem,
        keysAndValuesInUse: initialKeysAndValuesInUse,
        elems,
        cancelEditing,
        editItem,
        saveItem,
        setShapeHighlight,
        getActionType,
        loadingTaxonomies
    }) => {
    const [keysAndValuesInUse, setKeysAndValuesInUse] = useState(initialKeysAndValuesInUse);
    const [item, setItem] = useState(initialItem);

    useEffect(() => {
        setKeysAndValuesInUse(initialKeysAndValuesInUse);
    }, [initialKeysAndValuesInUse]);

    useEffect(() => {
        editItem(item);
    }, [item])

    useEffect(() => {
        const updates = {};
        if (!item.actions || item.actions.length === 0) {
            updates.actions = [[
                {lineId: null, direction: 'in', crossed: true},
                {lineId: null, direction: 'out', crossed: false}
            ]];
        }
        if (requiresMeta(item.type) && (!item.metadata || item.metadata.length === 0)) {
            updates.metadata = [{key: '', value: ''}];
        }
        if (Object.keys(updates).length) {
            setItem({...item, ...updates});
        }
    }, [item.type])

    return <Row>
        <Cell span={2} halign="center" valign="center">
            <Select
                popoverProps={{minimal: true}}
                items={["location-entry", "location-pass-by", "within-location-entry", "within-location-pass-by"]}
                filterable={false}
                itemRenderer={(tType, {handleClick}) => (
                    <MenuItem
                        key={tType}
                        text={<Tag minimal>{`${tType}`}</Tag>}
                        onClick={handleClick}
                    />
                )}
                noResults={<MenuItem disabled={true} text="No results."/>}
                onItemSelect={(tType) => setItem({...item, type: tType})}
            >
                <Tag minimal rightIcon="caret-down">{item.type || "Context Type"}</Tag>
            </Select>
        </Cell>
        <Cell span={3} haligh="center" valign="center">
            <Table>
                {item.actions?.map((action, actionIndex) => {
                    if (!action[0] || !action[1]) return null;
                    const actionsTuple = [action[0], action[1]];
                    const actionType = getActionType(actionsTuple);
                    return (
                        <Row key={actionIndex}>
                            <Cell halign="right" valign="center">
                                <Select
                                    popoverProps={{minimal: true}}
                                    items={elems.curves}
                                    filterable={false}
                                    itemRenderer={(elem, {handleClick}) => (
                                        <MenuItem
                                            key={elem.name}
                                            text={<Tag minimal intent="primary">{elem.name}</Tag>}
                                            onMouseEnter={() => setShapeHighlight(elem, true)}
                                            onMouseLeave={() => setShapeHighlight(elem, false)}
                                            onClick={handleClick}
                                        />
                                    )}
                                    noResults={<MenuItem disabled={true} text="No results."/>}
                                    onItemSelect={(elem) => {
                                        const newActions = [...item.actions];
                                        newActions[actionIndex] = [
                                            {...newActions[actionIndex][0], lineId: elem.name},
                                            {...newActions[actionIndex][1], lineId: elem.name}
                                        ];
                                        setItem({...item, actions: newActions});
                                    }}
                                >
                                    <Tag
                                        minimal rightIcon="caret-down"
                                        intent={action[0].lineId ? "primary" : "none"}
                                    >
                                        {action[0].lineId || "Select line"}
                                    </Tag>
                                </Select>
                            </Cell>
                            <Cell halign="left" valign="center">
                                <Select
                                    popoverProps={{minimal: true}}
                                    items={["in", "out", "in-out"]}
                                    filterable={false}
                                    itemRenderer={(direction, {handleClick}) => (
                                        <MenuItem
                                            key={direction}
                                            text={<Tag minimal>{`${direction}`}</Tag>}
                                            onClick={handleClick}
                                        />
                                    )}
                                    noResults={<MenuItem disabled={true} text="No results."/>}
                                    onItemSelect={(direction) => {
                                        const lineId = action[0].lineId;
                                        const base = direction === 'in' ? IN_ACTION
                                            : direction === 'out' ? OUT_ACTION
                                            : IN_AND_OUT_ACTION;
                                        const newActions = [...item.actions];
                                        newActions[actionIndex] = [
                                            {...base[0], lineId},
                                            {...base[1], lineId}
                                        ];
                                        setItem({...item, actions: newActions});
                                    }}
                                >
                                    <Tag minimal rightIcon="caret-down">{actionType || "Direction"}</Tag>
                                </Select>
                                {item.actions?.length > 1 &&
                                <Icon style={{paddingLeft: "2px"}} icon="cross" onClick={() => {
                                    item.actions.splice(actionIndex, 1)
                                    setItem({...item});
                                }}/>}
                            </Cell>
                        </Row>
                    )
                })}
                <Row>
                    <Cell haligh="center" valign="bottom">
                        <Button
                            small={true}
                            text="add line"
                            icon="plus"
                            fill={true}
                            onClick={() => {
                                const actions = item.actions ? item.actions : [];
                                actions.push([
                                    {lineId: null, direction: 'in', crossed: true},
                                    {lineId: null, direction: 'out', crossed: false}
                                ]);
                                item.actions = actions;
                                setItem({...item});
                            }}
                        />
                    </Cell>
                </Row>
            </Table>
        </Cell>
        <Cell span={3} haligh="center" valign="center">
            {(item.type === 'within-location-entry' || item.type === 'within-location-pass-by') &&
            <Table>
                {item.metadata?.map((meta, index) => {
                    return <Row key={index}>
                        <Cell key={index} halign="right" valign="center">
                            <Suggest
                                popoverProps={{minimal: true}}
                                inputProps={{small: true, fill: false}}
                                query={meta.key ?? ''}
                                items={!loadingTaxonomies && keysAndValuesInUse?.keys?.length ? keysAndValuesInUse.keys.sort() : []}
                                itemPredicate={(query, item) => !!item && item.toLowerCase().indexOf(query.toLowerCase()) >= 0}
                                inputValueRenderer={(val) => val}
                                itemRenderer={(key, {handleClick}) => (
                                    <MenuItem
                                        key={`${key}-${index}`}
                                        text={`${key}`}
                                        onClick={handleClick}
                                    />
                                )}
                                noResults={<MenuItem disabled={true} text="No results."/>}
                                onItemSelect={(key) => {
                                    const newMetadata = item.metadata.map((m, i) => i === index ? {...m, key} : m);
                                    setItem({...item, metadata: newMetadata});
                                }}
                                onQueryChange={(key) => {
                                    const newMetadata = item.metadata.map((m, i) => i === index ? {...m, key} : m);
                                    setItem({...item, metadata: newMetadata});
                                }}
                            />
                        </Cell>
                        <Cell halign="left" valign="center">
                            <Suggest
                                popoverProps={{minimal: true}}
                                inputProps={{small: true}}
                                query={meta.value ?? ''}
                                items={!loadingTaxonomies && keysAndValuesInUse?.values?.length ? keysAndValuesInUse.values.sort() : []}
                                itemPredicate={(query, item) => !!item && item.toLowerCase().indexOf(query.toLowerCase()) >= 0}
                                inputValueRenderer={(val) => val}
                                itemRenderer={(value, {handleClick}) => (
                                    <MenuItem
                                        key={`${value}-${index}`}
                                        text={`${value}`}
                                        onClick={handleClick}
                                    />
                                )}
                                noResults={<MenuItem disabled={true} text="No results."/>}
                                onItemSelect={(value) => {
                                    const newMetadata = item.metadata.map((m, i) => i === index ? {...m, value} : m);
                                    setItem({...item, metadata: newMetadata});
                                }}
                                onQueryChange={(value) => {
                                    const newMetadata = item.metadata.map((m, i) => i === index ? {...m, value} : m);
                                    setItem({...item, metadata: newMetadata});
                                }}
                            />
                            {!(requiresMeta(item.type) && item.metadata?.length <= 1) &&
                            <Icon style={{paddingLeft: "2px"}} icon="cross" onClick={() => {
                                item.metadata.splice(index, 1)
                                setItem({...item});
                            }}/>}
                        </Cell>
                    </Row>
                })}
                <Row>
                    <Cell haligh="center" valign="bottom">
                        <Button
                            small={true}
                            text="add meta"
                            icon="plus"
                            fill={true}
                            onClick={() => {
                                const metas = item.metadata ? item.metadata : [];
                                metas.push({key: '', value: ''});
                                item.metadata = metas;
                                setItem({...item});
                            }}
                        />
                    </Cell>
                </Row>
            </Table>}
        </Cell>
        <Cell span={1} halign="right" valign="center" gap="5px">
            <Fragment>
                <Icon
                    icon="floppy-disk"
                    htmlTitle="Save"
                    intent="success"
                    onClick={() => {
                        if (!item.actions?.length || item.actions.some(a => !a[0]?.lineId)) {
                            errorToast({message: 'Every action must have a line selected'});
                            return;
                        }
                        if (requiresMeta(item.type)) {
                            if (!item.metadata?.length) {
                                errorToast({message: 'At least one metadata entry is required'});
                                return;
                            }
                            const hasEmptyMeta = item.metadata?.some(m => !m.key?.trim() || !m.value?.trim());
                            if (hasEmptyMeta) {
                                errorToast({message: 'Metadata key and value cannot be empty'});
                                return;
                            }
                        }
                        saveItem(item);
                    }}
                />
                <Icon
                    icon="delete"
                    htmlTitle="Cancel"
                    intent="danger"
                    onClick={cancelEditing}
                />
            </Fragment>
        </Cell>
    </Row>
}

export {EditingItemForm};